import React, {FC} from 'react';
import {useInformerConfigState} from '@/informers/services/informerConfigReducer';
import {useLocalizer} from '@/common/services/localizer';
import {FieldSelect} from '@/common/components/FieldSelect';
import {InfoIcon} from '@/common/components/InfoIcon';
import {ColorPickButton} from '../ColorPickButton';
import styles from './FormView.css';

type ThemeOption = 'light' | 'dark' | 'custom';
const themeOptions: ThemeOption[] = ['light', 'dark', 'custom'];

const supportedFonts = [
	'arial',
	'helvetica',
	'times-new-roman',
	'courier',
	'verdana',
	'georgia',
	'trebuchet-ms',
	'roboto',
];

const fontNames: Record<string, string> = {
	arial: 'Arial',
	helvetica: 'Helvetica',
	'times-new-roman': 'Times New Roman',
	courier: 'Courier',
	verdana: 'Verdana',
	georgia: 'Georgia',
	'trebuchet-ms': 'Trebuchet MS',
	roboto: 'Roboto',
};

const nonDefaultFonts = ['helvetica', 'roboto', 'trebuchet-ms'];

const getFontName = (name: string) => fontNames[name];

interface FormViewProps {
	onNextStage(): void;
}

export const FormView: FC<FormViewProps> = props => {
	const {onNextStage} = props;

	const t = useLocalizer();

	const {state, dispatch} = useInformerConfigState();

	const handleComplete = (event: React.FormEvent) => {
		event.preventDefault();
		onNextStage();
	};
	const handleFontChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.currentTarget.value;
		dispatch({type: 'setValues', values: {fontFamily: value}});
	};

	const handleThemeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		const value = event.currentTarget.value as ThemeOption;
		dispatch({type: 'setValues', values: {theme: value}});
	};

	const getThemeOptionName = (option: string) => t(`themeOptions.${option}`);

	const isNonDefaultFont = nonDefaultFonts.includes(state.fontFamily);

	return (
		<form onSubmit={handleComplete}>
			<p className={styles.labelText}>
				{t('fontTitle')}
				{isNonDefaultFont && (
					<InfoIcon className={styles.labelInfo} title={t('fontExtraInfo')} positionX='right' />
				)}
			</p>
			<FieldSelect
				options={supportedFonts}
				value={state.fontFamily}
				getDisplayValue={getFontName}
				onChange={handleFontChange}
			/>

			<p className={styles.labelText}>{t('themeTitle')}</p>
			<FieldSelect
				options={themeOptions}
				value={state.theme}
				getDisplayValue={getThemeOptionName}
				onChange={handleThemeChange}
			/>

			{state.theme === 'custom' && (
				<>
					<p className={styles.labelText}>{t('themeColors')}</p>
					<div className={styles.colorsWrapper}>
						<div className={styles.doubleColorEntry}>
							<p className={styles.colorLabel}>{t('colorBg')}</p>
							<ColorPickButton
								className={styles.colorPicker}
								value={state.customFirstBgColor}
								onChange={customFirstBgColor => {
									dispatch({type: 'setValues', values: {customFirstBgColor}});
								}}
							/>
							<ColorPickButton
								className={styles.colorPicker}
								value={state.customSecondBgColor}
								onChange={customSecondBgColor => {
									dispatch({type: 'setValues', values: {customSecondBgColor}});
								}}
							/>
						</div>
						<div className={styles.singleColorEntry}>
							<p className={styles.colorLabel}>{t('colorIcons')}</p>
							<ColorPickButton
								className={styles.colorPicker}
								value={state.customIconsColor}
								onChange={customIconsColor => {
									dispatch({type: 'setValues', values: {customIconsColor}});
								}}
							/>
						</div>
						<div className={styles.doubleColorEntry}>
							<p className={styles.colorLabel}>{t('colorText')}</p>
							<ColorPickButton
								className={styles.colorPicker}
								value={state.customFirstTextColor}
								onChange={customFirstTextColor => {
									dispatch({type: 'setValues', values: {customFirstTextColor}});
								}}
							/>
							<ColorPickButton
								className={styles.colorPicker}
								value={state.customSecondTextColor}
								onChange={customSecondTextColor => {
									dispatch({type: 'setValues', values: {customSecondTextColor}});
								}}
							/>
						</div>
					</div>
				</>
			)}
		</form>
	);
};

FormView.displayName = 'FormView';
